import React, { useEffect, useMemo, useState } from "react";
import { onBackdropMouseDown, onModalPanelMouseDown } from "../lib/modal";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  CreditCard, Crown, Calendar, RefreshCw, ArrowUpRight, Search,
  CheckCircle2, AlertTriangle, PauseCircle, Clock, Loader2, X,
} from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || "Error";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((it) => typeof it === "string" ? it : (it?.msg || "")).join(" · ");
  return typeof d === "object" ? JSON.stringify(d) : String(d);
};

const STATUS_STYLES = {
  trial:     { bg: "bg-amber-100",  text: "text-amber-700",  label: { ar: "تجريبي", en: "Trial" }, Icon: Clock },
  active:    { bg: "bg-emerald-100", text: "text-emerald-700", label: { ar: "نشط", en: "Active" }, Icon: CheckCircle2 },
  suspended: { bg: "bg-rose-100",    text: "text-rose-700",    label: { ar: "موقوف", en: "Suspended" }, Icon: PauseCircle },
  expired:   { bg: "bg-orange-100",  text: "text-orange-700",  label: { ar: "منتهي", en: "Expired" }, Icon: AlertTriangle },
  archived:  { bg: "bg-stone-200",   text: "text-stone-600",   label: { ar: "مؤرشف", en: "Archived" }, Icon: PauseCircle },
};

const StatusBadge = ({ status, lang }) => {
  const s = STATUS_STYLES[status] || STATUS_STYLES.active;
  const Ico = s.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${s.bg} ${s.text}`}>
      <Ico size={11} />{s.label[lang === "ar" ? "ar" : "en"]}
    </span>
  );
};

// Single-row "assign plan / change plan / extend / status" modal.
// Kept on the same surface as the table so the operator never leaves the
// page for the most common subscription actions.
const ManageModal = ({ tenant, plans, onClose, onSaved, lang }) => {
  const isAr = lang === "ar";
  const [plan, setPlan] = useState(tenant.plan);
  const [status, setStatus] = useState(tenant.effective_status === "expired" ? "trial" : (tenant.status || "active"));
  const [extendDays, setExtendDays] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (plan && plan !== tenant.plan) payload.plan = plan;
      // Server-side status enum: trial | active | suspended | archived
      // (expired is virtual). We map "expired" → "trial" before sending.
      if (status && status !== "expired" && status !== tenant.status) payload.status = status;
      if (extendDays && parseInt(extendDays) > 0) payload.trial_extend_days = parseInt(extendDays);
      if (Object.keys(payload).length === 0) { onClose(); return; }
      await api.patch(`/admin/tenants/${tenant.tenant_id}`, payload);
      toast.success(isAr ? "تم الحفظ" : "Saved");
      onSaved();
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onMouseDown={(e) => onBackdropMouseDown(e, onClose)}>
      <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl" onMouseDown={onModalPanelMouseDown} data-testid="manage-sub-modal">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-heading text-xl gold-text">{isAr ? "إدارة الاشتراك" : "Manage Subscription"}</h2>
            <p className="text-xs text-stone-500 mt-0.5">{(tenant.name || {}).en || tenant.slug}</p>
          </div>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-700"><X size={20}/></button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-xs text-stone-500">{isAr ? "الخطة" : "Plan"}</label>
            <select className="input-dark mt-1" value={plan} onChange={(e) => setPlan(e.target.value)} data-testid="sub-plan">
              {plans.map((p) => {
                const code = p.id || p.code;
                return (
                  <option key={code} value={code}>{`${p.name_en} — ${p.monthly_price} ${p.currency || "QAR"}/mo`}</option>
                );
              })}
              {tenant.plan && !plans.some((p) => (p.id || p.code) === tenant.plan) && (
                <option value={tenant.plan}>{tenant.plan} (current legacy)</option>
              )}
            </select>
          </div>

          <div>
            <label className="text-xs text-stone-500">{isAr ? "الحالة" : "Status"}</label>
            <select className="input-dark mt-1" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="sub-status">
              <option value="trial">{isAr ? "تجريبي" : "Trial"}</option>
              <option value="active">{isAr ? "نشط" : "Active"}</option>
              <option value="suspended">{isAr ? "موقوف" : "Suspended"}</option>
            </select>
          </div>

          <div>
            <label className="text-xs text-stone-500">{isAr ? "تمديد فترة التجربة (أيام)" : "Extend trial (days)"}</label>
            <input type="number" min="0" className="input-dark mt-1" placeholder="14" value={extendDays} onChange={(e) => setExtendDays(e.target.value)} data-testid="sub-extend"/>
            <p className="text-[10px] text-stone-400 mt-1">{isAr ? "يضاف للتاريخ الحالي لانتهاء التجربة." : "Adds to the current trial-end date."}</p>
          </div>

          {tenant.trial_ends_at && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 flex items-center gap-2">
              <Calendar size={13}/>
              {isAr ? "ينتهي حالياً في:" : "Currently ends on:"} <span className="font-semibold">{new Date(tenant.trial_ends_at).toLocaleDateString(isAr ? "ar-EG" : "en-US")}</span>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-stone-300 text-stone-600 hover:bg-stone-100">{isAr ? "إلغاء" : "Cancel"}</button>
          <button onClick={save} disabled={saving} className="btn-gold px-5 py-2 rounded-lg flex items-center gap-2" data-testid="sub-save">
            {saving ? <Loader2 className="animate-spin" size={14}/> : <CheckCircle2 size={14}/>}{isAr ? "حفظ" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default function PlatformAdminSubscriptions() {
  const { lang } = useLanguage();
  const isAr = lang === "ar";

  const [items, setItems] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [manage, setManage] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get("/admin/tenants"),
        api.get("/admin/tenants/plans"),
      ]);
      setItems(a.data.items || []);
      setPlans(b.data.plans || []);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return items.filter((t) => {
      if (statusFilter && statusFilter !== t.effective_status) return false;
      if (!search) return true;
      const hay = `${(t.name || {}).en || ""} ${(t.name || {}).ar || ""} ${t.slug || ""} ${(t.contact || {}).owner_email || ""}`.toLowerCase();
      return hay.includes(search.toLowerCase());
    });
  }, [items, search, statusFilter]);

  return (
    <div className="p-6 sm:p-8 max-w-7xl mx-auto space-y-5" data-testid="platform-subscriptions">
      <div>
        <h1 className="font-heading text-3xl gold-text">{isAr ? "الاشتراكات" : "Subscriptions"}</h1>
        <p className="text-sm text-stone-500 mt-1">{isAr ? "إدارة خطط Tenants وحالاتها وتمديد الاشتراك" : "Manage tenant plans, statuses and trial extensions"}</p>
      </div>

      {/* Plans cheat-sheet — single read-only row so the operator always sees
          what each plan ships with. */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {plans.map((p) => {
          const fmt = (v) => v === null || v === undefined ? "∞" : v;
          return (
            <div key={p.id || p.code} className="bg-white rounded-2xl border border-stone-100 p-4 shadow-sm">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-stone-400">
                <Crown size={11}/>{p.id || p.code}
              </div>
              <div className="font-heading text-lg gold-text mt-1">{p.name_en}</div>
              <div className="text-2xl font-bold text-stone-800 num-display mt-1">{p.monthly_price} <span className="text-xs text-stone-400">QAR/mo</span></div>
              <ul className="text-[11px] text-stone-500 mt-2 space-y-0.5">
                <li>· {fmt(p.max_branches)} branches · {fmt(p.max_users)} users</li>
                <li>· {fmt(p.max_services)} services · {fmt(p.max_bookings_per_month)}/mo bookings</li>
              </ul>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-stone-100 p-3 shadow-sm flex flex-wrap gap-2 items-center">
        <div className="flex-1 min-w-[200px] relative">
          <Search size={14} className="absolute top-1/2 -translate-y-1/2 start-3 text-stone-400 pointer-events-none"/>
          <input
            className="input-dark ps-9 text-sm"
            placeholder={isAr ? "بحث..." : "Search..."}
            value={search} onChange={(e) => setSearch(e.target.value)}
            data-testid="sub-search"
          />
        </div>
        <select className="input-dark text-sm w-auto" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="sub-status-filter">
          <option value="">{isAr ? "كل الحالات" : "All statuses"}</option>
          <option value="trial">{isAr ? "تجريبي" : "Trial"}</option>
          <option value="active">{isAr ? "نشط" : "Active"}</option>
          <option value="suspended">{isAr ? "موقوف" : "Suspended"}</option>
          <option value="expired">{isAr ? "منتهي" : "Expired"}</option>
          <option value="archived">{isAr ? "مؤرشف" : "Archived"}</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-stone-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-stone-400 text-sm flex items-center justify-center gap-2"><Loader2 className="animate-spin" size={14}/>Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-stone-400 text-sm">{isAr ? "لا توجد بيانات" : "No data"}</div>
        ) : (
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-stone-500 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-start p-3">{isAr ? "Tenant" : "Tenant"}</th>
                  <th className="p-3">{isAr ? "الخطة" : "Plan"}</th>
                  <th className="p-3">QAR/mo</th>
                  <th className="p-3">{isAr ? "الحالة" : "Status"}</th>
                  <th className="p-3">{isAr ? "تاريخ الإنشاء" : "Started"}</th>
                  <th className="p-3">{isAr ? "ينتهي" : "Ends"}</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((t) => {
                  const planPrice = (t.plan_summary && t.plan_summary.monthly_price) || 0;
                  return (
                    <tr key={t.tenant_id} className="border-t border-stone-100 hover:bg-stone-50/60 transition" data-testid={`sub-row-${t.tenant_id}`}>
                      <td className="p-3">
                        <Link to={`/platform-admin/tenants/${t.tenant_id}`} className="font-semibold text-stone-800 hover:text-[var(--gold)] inline-flex items-center gap-1">
                          {(t.name || {}).en || t.slug}<ArrowUpRight size={11}/>
                        </Link>
                        <div className="text-[10px] text-stone-400">{(t.contact || {}).owner_email}</div>
                      </td>
                      <td className="p-3 text-center">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[var(--gold-tint)] text-[var(--gold)]"><Crown size={10}/>{t.plan}</span>
                      </td>
                      <td className="p-3 text-center text-stone-700 num-display font-semibold">{planPrice}</td>
                      <td className="p-3 text-center"><StatusBadge status={t.effective_status} lang={lang}/></td>
                      <td className="p-3 text-center text-xs text-stone-500">{t.created_at ? new Date(t.created_at).toLocaleDateString(isAr ? "ar-EG" : "en-US") : "—"}</td>
                      <td className="p-3 text-center text-xs text-stone-500">{t.trial_ends_at ? new Date(t.trial_ends_at).toLocaleDateString(isAr ? "ar-EG" : "en-US") : "—"}</td>
                      <td className="p-3 text-end">
                        <button onClick={() => setManage(t)} data-testid={`sub-manage-${t.tenant_id}`}
                          className="text-xs px-3 py-1.5 rounded-lg bg-[var(--gold-tint)] text-[var(--gold)] hover:bg-[var(--gold-tint-strong)] transition font-medium flex items-center gap-1 ms-auto">
                          <RefreshCw size={11}/>{isAr ? "إدارة" : "Manage"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {manage && <ManageModal tenant={manage} plans={plans} onClose={() => setManage(null)} onSaved={load} lang={lang}/>}
    </div>
  );
}
